/** Thin bar under the header: offline state + queued field actions, with a "Sync now" button. */
import { useEffect, useState } from "react";
import { CloudOff, RefreshCw, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { subscribe, flush, rejected, dropRejected } from "@/lib/offlineQueue";

export default function OfflineBanner() {
  const [items, setItems] = useState([]);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = () => { setOffline(false); toast.success("Back online — syncing what you did offline"); };
    const off = () => { setOffline(true); toast.warning("You're offline — punches, safety and reports will be saved and sent when you have signal"); };
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const unsub = subscribe((list) => { setItems(list); setTick((t) => t + 1); });
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); unsub(); };
  }, []);
  const sync = async () => { setBusy(true); try { const r = await flush(); if (r.sent) toast.success(`Synced ${r.sent} queued action(s)`); if (r.failed) toast.error(`${r.failed} action(s) were rejected by the server`); } finally { setBusy(false); } };
  const bad = rejected.length;
  if (!offline && !items.length && !bad) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs ${offline ? "bg-amber-500/15 text-amber-200" : "bg-sky-500/15 text-sky-100"}`} data-testid="offline-banner" data-tick={tick}>
      {offline ? <CloudOff className="h-3.5 w-3.5" /> : <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />}
      <span className="font-semibold">{offline ? "Offline" : "Online"}</span>
      {items.length > 0 && <span data-testid="offline-pending-count">· {items.length} action{items.length === 1 ? "" : "s"} waiting to sync ({items.map((i) => i.label).slice(0, 3).join(", ")}{items.length > 3 ? "…" : ""})</span>}
      {!items.length && !offline && bad === 0 && <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> all synced</span>}
      {bad > 0 && (
        <span className="inline-flex items-center gap-1 text-red-200" data-testid="offline-rejected">
          <AlertTriangle className="h-3.5 w-3.5" /> {bad} rejected: {rejected.map((r) => `${r.label} — ${r.error}`).join("; ")}
          <button onClick={() => { dropRejected(); setTick((t) => t + 1); }} className="ml-1 rounded bg-white/10 p-0.5" aria-label="Dismiss"><X className="h-3 w-3" /></button>
        </span>
      )}
      {!offline && items.length > 0 && <button onClick={sync} disabled={busy} className="ml-auto rounded-md bg-white/15 px-2 py-0.5 font-semibold hover:bg-white/25 disabled:opacity-50" data-testid="offline-sync-now">Sync now</button>}
    </div>
  );
}
